import { Fragment, useState } from "react"

// ** Third Party Components
import { format } from "date-fns"
import { Loader } from "lucide-react"
import { useMutation } from "@tanstack/react-query"
import { ArrowRight2, Like1 } from "iconsax-reactjs"

// ** Theme Components
import { Avatar, AvatarImage } from "@pma/components/common/avatar"

// ** Services
import {
  createUpdatedLogReplyNoteService,
  likeActivityService,
  unlikeActivityService,
} from "@pma/services/activities"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

interface UpdateLogCardProps {
  log: any
  onRefetch?: () => void
}

const UpdateLogCard = ({ log, onRefetch }: UpdateLogCardProps) => {
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"

  const [note, setNote] = useState<string>("")
  const [previousLikedState, setPreviousLikedState] = useState<boolean | undefined>(undefined)
  const [optimisticLiked, setOptimisticLiked] = useState<boolean | undefined>(undefined)

  const isLiked = optimisticLiked !== undefined ? optimisticLiked : log._is_liked

  const handleLikeAndUnlikeActivity = async (currentLikedState: boolean, notificationId: number) => {
    // Store previous state for rollback
    setPreviousLikedState(currentLikedState)

    // Optimistically update UI immediately when user clicks
    const newState = !currentLikedState
    setOptimisticLiked(newState)

    try {
      // Make API call
      if (currentLikedState) {
        // Currently liked - user wants to unlike
        await unlikeActivityService({ change_id: notificationId })
      } else {
        // Currently not liked - user wants to like
        await likeActivityService({ change_id: notificationId })
      }
      // Success - refetch to get fresh data
      onRefetch?.()
    } catch (error: any) {
      // Rollback only on error
      setOptimisticLiked(previousLikedState)
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Unable to perform this action"),
        variant: "error",
      })
    }
  }

  const createUpdateLogReplyNoteMutation = useMutation({
    mutationKey: ["create-update-log-reply-note"],
    mutationFn: createUpdatedLogReplyNoteService,
    onSuccess: (data) => {
      setNote("")
      toast({
        title: translate("Success"),
        description: translate("Note insert successfully"),
        variant: "success",
      })
    },
    onError: (error: any) => {
      toast({
        title: translate("Error"),
        description: translate(error?.message || "Error occurred"),
        variant: "error",
      })
    },
  })

  return (
    <div className='flex flex-col rounded-2xl border border-neutral-200'>
      <div className='flex flex-col border-b px-6 pb-6 pt-4'>
        <div className='flex gap-3'>
          <Avatar className='h-6 w-6'>
            <AvatarImage
              src={log?.user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`}
            />
          </Avatar>
          <div className='flex w-full flex-col gap-4'>
            <div className='flex w-full flex-col'>
              <div className='flex justify-between'>
                <div className='flex items-center gap-2'>
                  <p className='font-[inter] text-base font-bold text-neutral-800'>
                    {log.user?.name}
                  </p>
                  <div className='h-1 w-1 rounded-full bg-neutral-200' />
                  <p className='font-[inter] text-xs font-normal text-neutral-600'>
                    {format(new Date(log.created_at), "HH:mm")}
                  </p>
                </div>
                <div className='flex gap-1'>
                  <Like1
                    size='18'
                    variant={isLiked ? "Bold" : "Linear"}
                    className='cursor-pointer text-black'
                    onClick={() => handleLikeAndUnlikeActivity(isLiked, log.id)}
                  />
                </div>
              </div>
              <div className='flex items-center gap-1'>
                {log.breadcrumbs.map((item: any, index: number) => {
                  return (
                    <Fragment>
                      {index == log.breadcrumbs.length - 1 ? (
                        <span className='font-[inter] text-xs font-semibold text-indigo-500'>
                          {translate(item.title)}
                        </span>
                      ) : (
                        <div className='font-[inter] text-xs font-semibold text-[#BEBEC8]'>
                          {item.title}
                        </div>
                      )}
                      {index == log.breadcrumbs.length - 1 ? null : (
                        <ArrowRight2 className='h-3.5 w-3.5 text-neutral-400' />
                      )}
                    </Fragment>
                  )
                })}
              </div>
            </div>
            {log.note ? (
              <p
                className='max-w-md truncate font-[inter] text-sm font-normal text-neutral-800'
                dangerouslySetInnerHTML={{ __html: log.title }}
              />
            ) : (
              <p className='max-w-md truncate font-[inter] text-sm font-normal text-neutral-800'>
                {translate(log.title)}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className='flex border-t border-neutral-100 py-4 pl-6 pr-4'>
        <input
          className='w-full focus:outline-none'
          placeholder={`${translate("Write a reply and mention others with")} @`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          className={cn(
            "rounded-3xl bg-indigo-600 px-4 py-2 font-[inter] text-sm font-medium text-white shadow",
            isHebrew ? "mr-1" : "ml-1",
          )}
          disabled={note === "" || createUpdateLogReplyNoteMutation.isPending}
          onClick={() => {
            createUpdateLogReplyNoteMutation.mutate({
              note: JSON.stringify([{ insert: note }]),
              context_id: log.task_id,
              context_type: "task",
            })
          }}
        >
          {createUpdateLogReplyNoteMutation.isPending ? (
            <Loader className='animate-spin' />
          ) : (
            translate("Send")
          )}
        </button>
      </div>
    </div>
  )
}

export default UpdateLogCard
